import { RefObject, useLayoutEffect, useState } from "react";
import {
  sameStuckTableLayout,
  shouldFreezeAccountColumn,
  stickyChromeBottom,
  STUCK_TABLE_GAP_PX,
  stuckTableMaxHeight,
} from "../reviewLogic";
import { STICKY_CHROME_ATTR } from "components/nav/stickyChrome";

/** Below this width <main>, not the window, scrolls, so nothing sticks to it. */
const DESKTOP_QUERY = "(min-width: 768px)";

export type StuckTableLayout = { top: number; maxHeight: number };

const chromeBars = () =>
  Array.from(
    document.querySelectorAll<HTMLElement>(`[${STICKY_CHROME_ATTR}]`),
  ).map((el) => {
    const style = window.getComputedStyle(el);
    return {
      position: style.position,
      top: parseFloat(style.top) || 0,
      height: el.offsetHeight,
    };
  });

/**
 * Keeps a review table's header row and Account column in view (PLO-6216).
 *
 * `stuck` (the single table that fills the page): the page scrolls, not the
 * table, until the table is capped. So measure where the page's sticky chrome
 * ends and how much content sits under the table, then stick the table below
 * the chrome at a height that keeps its header clear of it even at the page's
 * full scroll. Measured, not hard-coded, because the chrome changes: the
 * navigator only shows for a multi-review campaign, and a state bar's message
 * wraps. Desktop only — on a phone <main> scrolls, so nothing sticks, and the
 * table keeps its unmeasured cap (see reviewTableMaxHeight).
 *
 * `freezeAccount`: whether the scrollport leaves room to freeze the Account
 * column (see shouldFreezeAccountColumn).
 */
export const useFrozenPanes = (
  stuck: boolean,
  groupRef: RefObject<HTMLElement>,
  scrollRef: RefObject<HTMLElement>,
  actionsWidth: number,
  // Changes whenever the observed scrollport or Account header cell can be
  // replaced (collapse, header layout, column set), so the effect re-runs and
  // observes the fresh elements.
  layoutKey: string,
) => {
  const [layout, setLayout] = useState<StuckTableLayout | null>(null);
  const [freezeAccount, setFreezeAccount] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const group = groupRef.current;
      const wrapper = group?.parentElement;
      let next: StuckTableLayout | null = null;
      if (stuck && wrapper && window.matchMedia(DESKTOP_QUERY).matches) {
        const top = stickyChromeBottom(chromeBars()) + STUCK_TABLE_GAP_PX;
        const below =
          document.documentElement.scrollHeight -
          (wrapper.getBoundingClientRect().bottom + window.scrollY);
        next = {
          top,
          maxHeight: stuckTableMaxHeight(window.innerHeight, top, below),
        };
      }
      setLayout((prev) => (sameStuckTableLayout(prev, next) ? prev : next));

      const scroller = scrollRef.current;
      const account = scroller?.querySelector<HTMLElement>(
        '[data-col="member"]',
      );
      setFreezeAccount(
        !!scroller &&
          !!account &&
          shouldFreezeAccountColumn(
            scroller.clientWidth,
            account.offsetWidth,
            actionsWidth,
          ),
      );
    };

    measure();
    // The body's height moves with anything that changes the content under
    // the table; the scrollport's width with a docked side panel; the Account
    // header cell's with a drag-resize.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    const scroller = scrollRef.current;
    if (scroller) observer.observe(scroller);
    const account = scroller?.querySelector('[data-col="member"]');
    if (account) observer.observe(account);
    document
      .querySelectorAll(`[${STICKY_CHROME_ATTR}]`)
      .forEach((el) => observer.observe(el));
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [stuck, groupRef, scrollRef, actionsWidth, layoutKey]);

  return { layout, freezeAccount };
};
